'use client';
import {cn} from '@/lib/utils';
import {useTranslations} from 'next-intl';
import {useEffect, useRef, useState} from 'react';
import {LazyImage} from '../tools/LazyImage';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '../ui/carousel';

export default function Gallery(Prop: {images: string[]}) {
  const {images} = Prop;
  const [isOpenCarousel, setIsOpenCarousel] = useState<boolean>(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const t = useTranslations('Gallery');
  const m = useTranslations('Manifest');
  const scrollToIndex = (index: number) => {
    carouselApi?.scrollTo(index, true);
  };

  const btnNext = useRef<HTMLButtonElement>(null);
  const onOffCarousel = (index: number) => {
    if (!isOpenCarousel) {
      scrollToIndex(index);
      btnNext.current?.focus();
    }
    setIsOpenCarousel(!isOpenCarousel);
  };

  const [title, setTitle] = useState<string>('');

  useEffect(() => {
    if (isOpenCarousel) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpenCarousel(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpenCarousel]);

  useEffect(() => {
    setTitle(document.title.replace(' | ' + m('name'), ''));
  }, []);

  return (
    <div className="questrial text-[18px]">
      <div
        className={cn(
          'fixed h-screen w-full bg-black/50 z-[100] top-0 left-0 transition-opacity duration-500 ease-in-out ',
          isOpenCarousel ? 'opacity-100 ' : 'scale-0 opacity-0 '
        )}
        onClick={() => {
          btnNext.current?.focus();
          onOffCarousel(0);
        }}
      >
        <div className="absolute right-5 top-5 p-2 z-20">
          <div className="">
            <div className="rounded-full bg-[#000000] w-8 h-8 flex justify-center items-center text-white cursor-pointer opacity-50 hover:opacity-100 transition-all duration-200">
              <span className="material-symbols outlined">close</span>
            </div>
          </div>
        </div>
        {/* carousel */}
        <Carousel
          setApi={setCarouselApi}
          opts={{loop: true}}
          className="w-full relative group"
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                <div className="flex flex-col items-center justify-center text-white ">
                  <div className="h-screen w-full overflow-hidden flex justify-center items-center ">
                    <div className="w-full flex justify-center items-center overflow-hidden">
                      <img
                        onClick={(e) => {
                          e.stopPropagation();
                          btnNext.current?.focus();
                        }}
                        className="max-w-[80vw] max-h-[80vh] object-contain"
                        src={image}
                        alt={t('image', {title})}
                      />
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 1 && (
            <div
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <CarouselNext
                ref={btnNext}
                className="absolute  right-10 opacity-50 md:opacity-0 group-hover:opacity-50 bg-[#000000] text-white border-none"
              />
              <CarouselPrevious className="absolute  left-10 opacity-50 md:opacity-0 group-hover:opacity-50 bg-[#000000] text-white border-none" />
            </div>
          )}
        </Carousel>
      </div>

      <div className="grid grid-cols-3 gap-1 mb-10">
        {images?.map((image, index) => (
          <button
            key={index}
            className={cn(
              'w-full object-cover opacity-90 hover:opacity-100 transition-all duration-200 cursor-pointer h-[133px] overflow-hidden'
            )}
            onClick={() => {
              onOffCarousel(index);
            }}
          >
            <LazyImage
              src={image}
              alt={t('expand')}
              className={'object-cover w-full h-full '}
              width={200}
              height={133}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
